import { useState, useEffect } from 'react';
import { apiGet } from '../client';
import type { ApiNote } from './types';
import { openNoteBody } from '@/services/NoteBodyService';
import { cacheNoteBody, getCachedNoteBody } from '@/services/NoteBodyCache';

interface VersionContentResponse {
  note: ApiNote;
}

export function useLoadVersionContent(
  noteId: string | null,
  contentId: number | null,
) {
  const [versionContent, setVersionContent] = useState<string | null>(null);
  const [versionCreatedAt, setVersionCreatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedVersion, setLoadedVersion] = useState<{ noteId: string; contentId: number } | null>(null);

  useEffect(() => {
    // Clear state whenever either input becomes null
    if (!noteId || contentId === null) {
      setVersionContent(null);
      setVersionCreatedAt(null);
      setError(null);
      setLoading(false);
      setLoadedVersion(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setLoadedVersion(null);

      try {
        const res = await apiGet<VersionContentResponse>(
          `/app/v1/note/${noteId}?content_id=${contentId}`,
        );

        if (!cancelled) {
          if (res.note.encrypted) {
            const revision = res.note.seq ?? 0;
            const cached = getCachedNoteBody(noteId, revision, contentId);
            const plaintext = cached ?? await openNoteBody(noteId, res.note.content);
            if (cancelled) return;
            cacheNoteBody(noteId, revision, plaintext, contentId);
            setVersionContent(plaintext);
          } else {
            setVersionContent(res.note.content);
          }
          setVersionCreatedAt(res.note.content_updated_at ?? null);
          setLoadedVersion({ noteId, contentId });
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? 'Failed to load version');
          setVersionContent(null);
          setVersionCreatedAt(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    // Cancel the in-flight request result if noteId/contentId changes before it resolves
    return () => {
      cancelled = true;
    };
  }, [noteId, contentId]);

  const isCurrentVersion = loadedVersion?.noteId === noteId && loadedVersion?.contentId === contentId;
  return {
    versionContent: isCurrentVersion ? versionContent : null,
    versionCreatedAt: isCurrentVersion ? versionCreatedAt : null,
    loading: loading || (contentId !== null && !isCurrentVersion && !error),
    error,
  };
}
